(() => {
  const allowedCharacters = /^[A-Za-z0-9 .,'&+-]+$/;
  const maxLength = 24;

  const getOption = (element) => element?.closest?.('[data-engraving-option]');
  const getForm = (option) => option.closest('form[data-type="add-to-cart-form"]');
  const defaultEngravingSummary = 'We will collect names or initials before this item is added.';
  let modalIndex = 0;

  const getExistingModal = (option) => {
    if (!option) return null;
    const optionId = option.dataset.engravingOptionId;
    if (optionId) {
      const ownedModal = document.querySelector(`[data-engraving-modal][data-engraving-owner="${optionId}"]`);
      if (ownedModal) return ownedModal;
    }
    return option.querySelector('[data-engraving-modal]');
  };

  const getModal = (option) => {
    const modal = getExistingModal(option);
    if (!modal) return null;

    if (!option.dataset.engravingOptionId) {
      modalIndex += 1;
      option.dataset.engravingOptionId = `engraving-option-${Date.now()}-${modalIndex}`;
    }

    modal.dataset.engravingOwner = option.dataset.engravingOptionId;

    if (modal.parentElement !== document.body) {
      document.body.append(modal);
    }

    return modal;
  };

  const getOptionByModal = (modal) => {
    const owner = modal?.dataset?.engravingOwner;
    return owner ? document.querySelector(`[data-engraving-option][data-engraving-option-id="${owner}"]`) : null;
  };

  const getOptionFromTarget = (target) => {
    const option = getOption(target);
    if (option) return option;
    const modal = target?.closest?.('[data-engraving-modal]');
    return getOptionByModal(modal);
  };

  const getControlForm = (control) => {
    if (control.form?.matches?.('form[data-type="add-to-cart-form"]')) return control.form;
    const linkedForm = control.getAttribute('form');
    if (linkedForm) return document.getElementById(linkedForm);
    return control.closest('form[data-type="add-to-cart-form"]');
  };

  const getQuantity = (form) => {
    const value = Number(new FormData(form).get('quantity') || 1);
    return Number.isFinite(value) && value > 0 ? Math.min(Math.floor(value), 50) : 1;
  };

  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const getVariantMap = (option) => {
    if (option.variantMap) return option.variantMap;

    const script = option.querySelector('[data-engraving-variants]');
    try {
      const variants = JSON.parse(script?.textContent || '[]');
      option.variantMap = new Map(variants.map((variant) => [String(variant.id), variant]));
    } catch (error) {
      option.variantMap = new Map();
    }

    return option.variantMap;
  };

  const getSelectedVariant = (form, option) => {
    const variantId = form.querySelector('[name="id"]')?.value;
    return getVariantMap(option).get(String(variantId));
  };

  const selectedOptionText = (variant) => {
    const parts = [variant?.title, ...(variant?.options || [])];
    return normalize(parts.filter(Boolean).join(' '));
  };

  const noEngravingKeywords = (option) =>
    (option.dataset.noEngravingKeywords || '')
      .split('|')
      .map(normalize)
      .filter(Boolean);

  const requiresEngraving = (form, option) => {
    const variant = getSelectedVariant(form, option);
    const optionText = selectedOptionText(variant);
    const isNoEngravingVariant = noEngravingKeywords(option).some((keyword) => optionText.includes(keyword));
    return !isNoEngravingVariant;
  };

  const setError = (option, message = '') => {
    const error = getExistingModal(option)?.querySelector('[data-engraving-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const setSummary = (option, message) => {
    const summary = option.querySelector('[data-engraving-summary]');
    if (summary) summary.textContent = message;
  };

  const updateEngravingAvailability = (form) => {
    const option = form.querySelector('[data-engraving-option]');
    if (!option) return true;

    const required = requiresEngraving(form, option);
    const engravingNotice = option.querySelector('.engraving-option__notice:not([data-no-engraving-notice])');
    const noEngravingNotice = option.querySelector('[data-no-engraving-notice]');

    option.dataset.engravingRequired = String(required);
    if (engravingNotice) engravingNotice.hidden = !required;
    if (noEngravingNotice) noEngravingNotice.hidden = required;

    if (!required) {
      clearProperties(option);
      option.dataset.engravingConfirmed = 'false';
      setError(option);
      closeModal(option);
    } else if (option.dataset.engravingConfirmed !== 'true') {
      setSummary(option, defaultEngravingSummary);
    }

    return required;
  };

  const openModal = (option) => {
    const modal = getModal(option);
    if (!modal) return;

    modal.hidden = false;
    document.body.classList.add('engraving-modal-open');
    window.setTimeout(() => modal.querySelector('[data-engraving-text]')?.focus(), 50);
  };

  const closeModal = (option) => {
    const modal = getExistingModal(option);
    if (!modal) return;

    modal.hidden = true;
    document.body.classList.remove('engraving-modal-open');
  };

  const addHiddenProperty = (container, name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    container.append(input);
  };

  const saveNoEngraving = (option, quantity = 1) => {
    const properties = option.querySelector('[data-engraving-properties]');
    if (!properties) return;

    clearProperties(option);

    for (let index = 1; index <= quantity; index += 1) {
      const label = quantity === 1 ? 'Engraving' : `Engraving ${index}`;
      addHiddenProperty(properties, `${label} Type`, 'No engraving');
      addHiddenProperty(properties, `${label} Text`, 'No engraving requested');
    }

    option.dataset.engravingConfirmed = 'true';
    option.dataset.engravingQuantity = String(quantity);
    setSummary(option, quantity === 1 ? 'No engraving selected' : `${quantity} items: no engraving selected`);
  };

  const renderRows = (option, quantity) => {
    const modal = getModal(option);
    const items = modal?.querySelector('[data-engraving-items]');
    if (!items || Number(option.dataset.engravingQuantity || 0) === quantity) return;

    items.replaceChildren();

    for (let index = 1; index <= quantity; index += 1) {
      const row = document.createElement('div');
      row.className = 'engraving-item';
      row.innerHTML = `
        <p class="engraving-item__heading">Item ${index} of ${quantity}</p>
        <fieldset class="engraving-modal__choices">
          <legend class="visually-hidden">Engraving type for item ${index}</legend>
          <label class="engraving-choice">
            <input type="radio" name="engraving-choice-${index}" value="Names" checked data-engraving-type>
            <span>Names</span>
          </label>
          <label class="engraving-choice">
            <input type="radio" name="engraving-choice-${index}" value="Initials" data-engraving-type>
            <span>Initials</span>
          </label>
          <label class="engraving-choice">
            <input type="radio" name="engraving-choice-${index}" value="No engraving" data-engraving-type>
            <span>No engraving</span>
          </label>
        </fieldset>
        <div class="field engraving-modal__field">
          <input
            id="EngravingText-${index}"
            class="field__input engraving-modal__input"
            type="text"
            maxlength="${maxLength}"
            autocomplete="off"
            data-engraving-text
            placeholder="${index === 1 ? 'Ava & Leo' : 'A.B.'}"
          >
          <label class="field__label" for="EngravingText-${index}">Engraving text for item ${index}</label>
        </div>
        <div class="engraving-modal__meta">
          <span data-engraving-count>0/${maxLength} characters</span>
          <span>Letters, numbers, spaces and basic punctuation only</span>
        </div>
      `;
      items.append(row);
    }

    option.dataset.engravingQuantity = String(quantity);
    option.dataset.engravingConfirmed = 'false';
  };

  const updateCount = (input) => {
    const row = input.closest('.engraving-item');
    const count = row?.querySelector('[data-engraving-count]');
    if (count) count.textContent = `${input.value.length}/${maxLength} characters`;
  };

  const clearProperties = (option) => {
    const properties = option.querySelector('[data-engraving-properties]');
    if (properties) properties.replaceChildren();
  };

  const validateRows = (option) => {
    const modal = getExistingModal(option);
    const rows = [...(modal?.querySelectorAll('.engraving-item') || [])];
    const engravings = [];

    for (const [position, row] of rows.entries()) {
      const input = row.querySelector('[data-engraving-text]');
      const text = input.value.trim().replace(/\s+/g, ' ');
      const type = row.querySelector('[data-engraving-type]:checked')?.value || 'Names';

      if (type === 'No engraving') {
        engravings.push({ type, text: 'No engraving requested' });
        continue;
      }

      if (!text) {
        setError(option, `Please enter the engraving for item ${position + 1}.`);
        input.focus();
        return null;
      }

      if (!allowedCharacters.test(text)) {
        setError(option, `Please use letters, numbers, spaces and basic punctuation only for item ${position + 1}.`);
        input.focus();
        return null;
      }

      engravings.push({ type, text });
    }

    setError(option);
    return engravings;
  };

  const saveEngraving = (option) => {
    const engravings = validateRows(option);
    const properties = option.querySelector('[data-engraving-properties]');
    if (!engravings || !properties) return false;

    clearProperties(option);

    engravings.forEach((engraving, index) => {
      const label = engravings.length === 1 ? 'Engraving' : `Engraving ${index + 1}`;
      addHiddenProperty(properties, `${label} Type`, engraving.type);
      addHiddenProperty(properties, `${label} Text`, engraving.text);
    });

    option.dataset.engravingConfirmed = 'true';
    setSummary(
      option,
      engravings.length === 1
        ? `${engravings[0].type}: ${engravings[0].text}`
        : `${engravings.length} engraving details saved`
    );
    closeModal(option);
    return true;
  };

  const beginRequiredEngraving = (form, submitter = null) => {
    const option = form.querySelector('[data-engraving-option]');
    if (!option) return false;

    const quantity = getQuantity(form);
    if (!updateEngravingAvailability(form)) {
      saveNoEngraving(option, quantity);
      return true;
    }

    renderRows(option, quantity);

    if (option.dataset.engravingConfirmed === 'true' && Number(option.dataset.engravingQuantity) === quantity) {
      return saveEngraving(option);
    }

    option.pendingSubmitter = submitter;
    openModal(option);
    return false;
  };

  const continueSubmit = (form, submitter) => {
    if (!form) return;

    if (form.requestSubmit) {
      try {
        form.requestSubmit(submitter || undefined);
        return;
      } catch (error) {
        form.requestSubmit();
        return;
      }
    }

    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  };

  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-engraving-text]')) return;

    const option = getOptionFromTarget(event.target);
    if (option) {
      option.dataset.engravingConfirmed = 'false';
      clearProperties(option);
      setSummary(option, defaultEngravingSummary);
      setError(option);
    }
    updateCount(event.target);
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-engraving-type]')) return;

    const option = getOptionFromTarget(event.target);
    if (!option) return;

    option.dataset.engravingConfirmed = 'false';
    clearProperties(option);
    setSummary(option, defaultEngravingSummary);
  });

  document.addEventListener('change', (event) => {
    const form = getControlForm(event.target);
    if (form) window.setTimeout(() => updateEngravingAvailability(form), 0);
  });

  document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach(updateEngravingAvailability);

  document.addEventListener('click', (event) => {
    const option = getOptionFromTarget(event.target);
    if (!option) return;

    if (event.target.closest('[data-engraving-save]')) {
      if (!saveEngraving(option)) return;

      const form = getForm(option);
      const submitter = option.pendingSubmitter instanceof HTMLElement ? option.pendingSubmitter : null;
      option.pendingSubmitter = null;

      continueSubmit(form, submitter);
    }

    if (event.target.closest('[data-engraving-close]')) {
      option.pendingSubmitter = null;
      closeModal(option);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('[data-engraving-modal]:not([hidden])').forEach((modal) => {
      const option = getOptionByModal(modal);
      if (!option) return;
      option.pendingSubmitter = null;
      closeModal(option);
    });
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches('form[data-type="add-to-cart-form"]')) return;

      if (beginRequiredEngraving(form, event.submitter)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
})();
